import { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { optimizedImageUrl } from '@/lib/imageOptimizer';
import { cn } from '@/lib/utils';

export interface ListingCardItem {
  id?: string;
  imageUrl?: string;
  hot?: boolean;
  topRightChip?: string;
  priceLabel: string;
  title: string;
  subtitleLink?: string;
  icons?: Array<{ icon: React.ReactNode; label: string | number }>;
  onClick?: () => void;
  onSubtitleClick?: () => void;
  footer?: React.ReactNode;
  agencyLogoUrl?: string | null;
  agencyName?: string | null;
}

export function HorizontalListingCard({
  imageUrl, hot, topRightChip, priceLabel, title, subtitleLink, icons, onClick, onSubtitleClick, footer, agencyLogoUrl, agencyName,
}: ListingCardItem) {
  const src = optimizedImageUrl(
    imageUrl || 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=400',
    { width: 400, height: 300, quality: 75 },
  );
  return (
    <div
      onClick={onClick}
      className="snap-start flex-shrink-0 w-[46%] sm:w-[260px] rounded-xl border bg-card overflow-hidden hover:shadow-lg cursor-pointer group"
    >
      <div className="relative aspect-[4/3]">
        <img src={src} alt={title} loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {hot && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-destructive text-destructive-foreground text-[10px] font-bold rounded uppercase">
            HOT
          </span>
        )}
        {topRightChip && (
          <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-black/70 text-white text-[9px] font-semibold uppercase tracking-wide">
            {topRightChip}
          </span>
        )}
      </div>
      <div className="p-2.5 space-y-1">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0 space-y-1">
            <div className="font-bold text-sm leading-tight">{priceLabel}</div>
            <div className="font-medium text-xs leading-tight line-clamp-1 text-muted-foreground">{title}</div>
            {subtitleLink && (
              <button
                onClick={(e) => { e.stopPropagation(); onSubtitleClick?.(); }}
                className="text-primary text-xs font-semibold hover:underline block text-left"
              >
                {subtitleLink}
              </button>
            )}
            {icons && icons.length > 0 && (
              <div className="flex items-center gap-3 pt-1 text-[11px] text-muted-foreground">
                {icons.map((it, i) => (
                  <span key={i} className="flex items-center gap-1">
                    {it.icon}
                    <span>{it.label}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          {(agencyLogoUrl || agencyName) && (
            <div
              className="shrink-0 h-14 w-14 rounded-full border bg-background flex items-center justify-center overflow-hidden shadow-sm"
              title={agencyName || 'Agency'}
              aria-label={agencyName || 'Agency'}
            >
              {agencyLogoUrl ? (
                <img
                  src={optimizedImageUrl(agencyLogoUrl, { width: 96, height: 96, quality: 80 })}
                  alt={agencyName || 'Agency logo'}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <span className="text-xs font-bold text-primary uppercase">
                  {(agencyName || '?').slice(0, 2)}
                </span>
              )}
            </div>
          )}
        </div>
        {footer}
      </div>
    </div>
  );
}

interface RowProps {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function HorizontalCardRow({ title, icon, children, className }: RowProps) {
  const ref = useRef<HTMLDivElement>(null);
  const scroll = (dir: 'left' | 'right') => {
    if (!ref.current) return;
    const w = ref.current.clientWidth * 0.6;
    ref.current.scrollBy({ left: dir === 'left' ? -w : w, behavior: 'smooth' });
  };
  return (
    <section className={cn('container py-4 md:py-6', className)}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="font-display text-lg md:text-xl font-bold">{title}</h2>
        </div>
      </div>
      <div className="relative">
        <button onClick={() => scroll('left')} aria-label={`Scroll ${title} left`}
          className="hidden sm:flex absolute -left-2 top-[35%] -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/90 border shadow items-center justify-center">
          <ChevronLeft className="h-4 w-4 text-primary" />
        </button>
        <button onClick={() => scroll('right')} aria-label={`Scroll ${title} right`}
          className="hidden sm:flex absolute -right-2 top-[35%] -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-background/90 border shadow items-center justify-center">
          <ChevronRight className="h-4 w-4 text-primary" />
        </button>
        <div ref={ref} className="flex gap-3 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-2 scroll-smooth">
          {children}
        </div>
      </div>
    </section>
  );
}

export function formatTimeAgo(dateStr?: string | null): string {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diffMs = Date.now() - d.getTime();
  const day = 86400000;
  const days = Math.floor(diffMs / day);
  if (days < 1) return 'TODAY';
  if (days < 7) return `${days} DAY${days > 1 ? 'S' : ''} AGO`;
  if (days < 30) {
    const w = Math.floor(days / 7);
    return `${w} WEEK${w > 1 ? 'S' : ''} AGO`;
  }
  if (days < 365) {
    const m = Math.floor(days / 30);
    return `${m} MONTH${m > 1 ? 'S' : ''} AGO`;
  }
  const y = Math.floor(days / 365);
  return `${y} YEAR${y > 1 ? 'S' : ''} AGO`;
}
